// System-wide credit management via server APIs

export type TokenRate = { input: number; output: number };
export type CreditSettings = {
  startBalance: number;
  autoRefill: boolean;
  refillBelow: number;
  refillAmount: number;
  estimateCharsPerToken: number;
  minCompletionReserve: number;
  defaultRate: TokenRate;
  modelRates: Record<string, TokenRate>;
  modelLimits?: Record<string, number>;
};

function isBrowser() {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

export function getUserId(): string {
  if (!isBrowser()) return 'server';
  const email = localStorage.getItem('user_email');
  const name = localStorage.getItem('user_name');
  const token = localStorage.getItem('auth_token');
  return email || name || token || 'guest';
}

export async function getSettings(): Promise<CreditSettings> {
  const res = await fetch('/api/credits/settings', { cache: 'no-store' });
  if (!res.ok) throw new Error(`settings ${res.status}`);
  return res.json();
}

export async function saveSettings(next: Partial<CreditSettings>): Promise<void> {
  const res = await fetch('/api/credits/settings', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(next),
  });
  if (!res.ok) {
    let msg = `save settings ${res.status}`;
    try {
      const data = await res.json();
      if (data?.message) msg = `save settings ${res.status}: ${String(data.message)}`;
    } catch {}
    throw new Error(msg);
  }
  try { window.dispatchEvent(new CustomEvent('credits-update', { detail: { type: 'settings' } })); } catch {}
}

export async function ensureUserInitialized(): Promise<void> {
  // No-op in system-wide mode
}

function metaHeaders(): Record<string, string> {
  if (!isBrowser()) return {};
  const name = localStorage.getItem('user_name') || '';
  const email = localStorage.getItem('user_email') || '';
  const h: Record<string, string> = {};
  if (name) h['x-user-name'] = name;
  if (email) h['x-user-email'] = email;
  return h;
}

export async function getBalance(forUserId?: string): Promise<number> {
  const uid = forUserId || getUserId();
  const res = await fetch('/api/credits/state', { cache: 'no-store', headers: { 'x-user-id': uid, ...metaHeaders() } as any });
  if (!res.ok) throw new Error(`state ${res.status}`);
  const data = await res.json();
  try {
    if (!isBrowser()) return Number(data?.balance || 0);
    const name = (data?.name || '').trim();
    const email = (data?.email || '').trim();
    if (name) localStorage.setItem('user_name', name);
    if (email && !localStorage.getItem('user_email')) localStorage.setItem('user_email', email);
  } catch {}
  return Number(data?.balance || 0);
}

export async function addCredit(amount: number, forUserId?: string): Promise<number> {
  const uid = forUserId || getUserId();
  const res = await fetch('/api/credits/add', { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-user-id': uid } as any, body: JSON.stringify({ amount }) });
  if (!res.ok) throw new Error(`add ${res.status}`);
  const data = await res.json();
  try { window.dispatchEvent(new CustomEvent('credits-update', { detail: { type: 'balance' } })); } catch {}
  return Number(data?.balance || 0);
}

export async function deductCredit(amount: number, forUserId?: string): Promise<number> {
  const uid = forUserId || getUserId();
  const res = await fetch('/api/credits/deduct', { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-user-id': uid } as any, body: JSON.stringify({ amount }) });
  if (!res.ok) throw new Error(`deduct ${res.status}`);
  const data = await res.json();
  try { window.dispatchEvent(new CustomEvent('credits-update', { detail: { type: 'balance' } })); } catch {}
  return Number(data?.balance || 0);
}

export async function setCreditLimit(limit: number, forUserId?: string): Promise<number> {
  const uid = forUserId || getUserId();
  const res = await fetch('/api/credits/limit', { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-user-id': uid } as any, body: JSON.stringify({ limit }) });
  if (!res.ok) throw new Error(`limit ${res.status}`);
  const data = await res.json();
  try { window.dispatchEvent(new CustomEvent('credits-update', { detail: { type: 'limit' } })); } catch {}
  return Number(data?.limit || 0);
}

export async function getCreditLimit(forUserId?: string): Promise<number> {
  const uid = forUserId || getUserId();
  const res = await fetch('/api/credits/state', { cache: 'no-store', headers: { 'x-user-id': uid, ...metaHeaders() } as any });
  if (!res.ok) throw new Error(`state ${res.status}`);
  const data = await res.json();
  return Number(data?.limit || 0);
}

export async function estimatePromptTokens(text: string): Promise<number> {
  try {
    const s = await getSettings();
    const cpt = Math.max(1, Number(s.estimateCharsPerToken || 4));
    const len = (text || '').length;
    return Math.max(1, Math.ceil(len / cpt));
  } catch {
    const len = (text || '').length;
    return Math.max(1, Math.ceil(len / 4));
  }
}

export async function precheckCanSend(args: { promptText: string; model?: string | null; }): Promise<{ ok: boolean; needed: number; balance: number; cost: number; reason?: string }> {
  const uid = getUserId();
  const [stateRes, settings] = await Promise.all([
    fetch('/api/credits/state', { cache: 'no-store', headers: { 'x-user-id': uid, ...metaHeaders() } as any }),
    getSettings(),
  ]);
  if (!stateRes.ok) throw new Error(`state ${stateRes.status}`);
  const state = await stateRes.json();
  const balance = Number(state?.balance || 0);
  const limit = Number(state?.limit || 0);
  const estPrompt = await estimatePromptTokens(args.promptText || '');
  const reserve = Math.max(0, Number(settings.minCompletionReserve || 0));
  const rate = (args.model && settings.modelRates[args.model]) || settings.defaultRate;
  const estCost = Math.floor(estPrompt * (rate.input || 0) + reserve * (rate.output || 0));
  const available = balance + Math.max(0, limit);
  return { ok: available >= estCost, needed: estCost, balance, cost: estCost, reason: available >= estCost ? undefined : 'insufficient-credit' };
}

export async function applyUsageCost(usage: { prompt: number; completion: number; total: number }, ctx?: { model?: string | null; conversationId?: string | number | null }) {
  try {
    const uid = getUserId();
    const res = await fetch('/api/credits/charge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-user-id': uid } as any,
      body: JSON.stringify({ prompt: usage.prompt || 0, completion: usage.completion || 0, model: ctx?.model || null }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return { cost: Number(data?.cost || 0), balance: 0 };
    }
    const data = await res.json();
    try { window.dispatchEvent(new CustomEvent('credits-update', { detail: { type: 'charge', balance: data?.balance } })); } catch {}
    return { cost: Number(data?.cost || 0), balance: Number(data?.balance || 0) };
  } catch {
    return { cost: 0, balance: 0 };
  }
}
